import React, { useState } from "react";
import contact from '../assets/images/solar/conta.webp'
import { FiPhone, FiMail, FiMapPin, FiClock } from 'react-icons/fi';
import { sendEnquiry, mediaUrl, sectionBlocks, useSiteData } from "../lib/siteApi";
import HtmlContent, { PhotoLink } from "./HtmlContent";

export default function GetInTouch({ bare = false }){
    const site = useSiteData();
    const block = sectionBlocks(site.content, "contact")[0];
    const image = mediaUrl(block?.imageUrl) || contact;
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [status, setStatus] = useState("");
    const [busy, setBusy] = useState(false);

    async function onSubmit(e) {
        e.preventDefault();
        setBusy(true);
        setStatus("");
        try {
            await sendEnquiry({ ...form, source: "website" });
            setStatus("Thank you. Your message was sent.");
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            setStatus(err.message || "Could not send message. Please email us instead.");
        } finally {
            setBusy(false);
        }
    }

    return(
        <section className={`relative bg-transparent ${bare ? "py-0" : "lg:py-24 py-16 bg-white dark:bg-slate-900"}`} id="contact">
            <div className={bare ? "relative" : "container relative"}>
                {bare ? null : (
                <div className="grid grid-cols-1 pb-6 text-center">
                    <h2 className="renerg-section text-slate-900 dark:text-white mb-4">{block?.title || "Get in touch"}</h2>
                    {block?.body ? <HtmlContent html={block.body} className="text-slate-500" imageTo="/#contact" /> : null}
                </div>
                )}

                <div className="grid md:grid-cols-12 grid-cols-1 items-center gap-6">
                    <div className="lg:col-span-6 md:col-span-6">
                        <PhotoLink to="/contact" src={image} alt="Contact RENERG" className="rounded-xl shadow-lg" wrapClassName="rounded-xl" />
                    </div>

                    <div className="lg:col-span-6 md:col-span-6">
                        <div className="lg:ms-5">
                            <div className="bg-white dark:bg-slate-900 rounded-xl shadow-lg dark:shadow-gray-700 p-6 lg:p-8">
                                <h3 className="renerg-h3 text-slate-900 dark:text-white mb-6">Contact Information</h3>
                                
                                <div className="space-y-6">
                                    {/* Phone */}
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-teal-50 dark:bg-teal-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FiPhone className="text-teal-600 dark:text-teal-400 text-xl" />
                                        </div>
                                        <div>
                                            <h5 className="font-semibold text-slate-900 dark:text-white mb-1">Phone</h5>
                                            <a href="tel:+250791502063" className="text-slate-600 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors duration-200">
                                                +250 791 502 063
                                            </a>
                                        </div>
                                    </div>

                                    {/* Email */}
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-teal-50 dark:bg-teal-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FiMail className="text-teal-600 dark:text-teal-400 text-xl" />
                                        </div>
                                        <div>
                                            <h5 className="font-semibold text-slate-900 dark:text-white mb-1">Email</h5>
                                            <a href="mailto:renergrwanda@gmail.com" className="text-slate-600 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors duration-200 break-all">
                                                renergrwanda@gmail.com
                                            </a>
                                        </div>
                                    </div>

                                    {/* Address */}
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-teal-50 dark:bg-teal-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FiMapPin className="text-teal-600 dark:text-teal-400 text-xl" />
                                        </div>
                                        <div>
                                            <h5 className="font-semibold text-slate-900 dark:text-white mb-1">Headquarters</h5>
                                            <p className="text-slate-600 dark:text-slate-400">
                                                Nyarugenge, Kigali<br/>
                                                Rwanda, East Africa
                                            </p>
                                        </div>
                                    </div>

                                    {/* Business Hours */}
                                    <div className="flex items-start gap-4">
                                        <div className="w-12 h-12 bg-teal-50 dark:bg-teal-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <FiClock className="text-teal-600 dark:text-teal-400 text-xl" />
                                        </div>
                                        <div>
                                            <h5 className="font-semibold text-slate-900 dark:text-white mb-1">Business Hours</h5>
                                            <p className="text-slate-600 dark:text-slate-400">
                                                Monday - Friday: 8:00 AM - 5:00 PM<br/>
                                                Saturday: 9:00 AM - 1:00 PM
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Actions */}
                                <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-700">
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        <a 
                                            href="mailto:renergrwanda@gmail.com" 
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg transition-all duration-200"
                                        >
                                            <FiMail className="text-lg" />
                                            Email Us
                                        </a>
                                        <a 
                                            href="tel:+250791502063" 
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-semibold rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition-all duration-200"
                                        >
                                            <FiPhone className="text-lg" />
                                            Call Now
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <form onSubmit={onSubmit} className="mt-10 max-w-3xl mx-auto bg-white dark:bg-slate-900 rounded-xl shadow-lg p-6 lg:p-8">
                    <h3 className="renerg-h4 mb-4 text-slate-900">Send an enquiry</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                        <input className="border rounded-lg px-3 py-2" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        <input className="border rounded-lg px-3 py-2" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        <input className="border rounded-lg px-3 py-2" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        <input className="border rounded-lg px-3 py-2" placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
                    </div>
                    <textarea className="border rounded-lg px-3 py-2 mt-4 w-full" required rows="4" placeholder="How can we help?" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} />
                    <button disabled={busy} className="mt-4 px-6 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-lg">
                        {busy ? "Sending..." : "Send message"}
                    </button>
                    {status && <p className="mt-3 text-sm text-teal-700">{status}</p>}
                </form>
            </div>
        </section>
    )
}